import { CbhIcon, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { SHIFT_DISCOVERY_WORKPLACE_PROFILE_FULL_PATH } from "@src/appV2/redesign/ShiftDiscovery/paths";
import { generatePath, useHistory, useLocation } from "react-router-dom";

import { IconButton } from "../components/IconButton";

interface WorkplaceChatHeaderProps {
  workplaceName?: string;
  channelShiftTime?: string;
  workplaceId: string;
  fallbackGoBackPath: string;
}

export function WorkplaceChatHeader(props: WorkplaceChatHeaderProps) {
  const { workplaceName, channelShiftTime, workplaceId, fallbackGoBackPath } = props;
  const history = useHistory();
  const location = useLocation();

  const getTitleVariant = (
    workplaceName: string | undefined
  ): "h6" | "body1" | "body2" | "caption" => {
    if (!workplaceName) {
      return "h6";
    }

    const { length } = workplaceName;

    if (length < 30) {
      return "h6";
    }

    if (length < 40) {
      return "body1";
    }

    if (length < 50) {
      return "body2";
    }

    return "caption";
  };

  const handleViewFacilityProfile = () => {
    logEvent(APP_V2_USER_EVENTS.VIEWED_FACILITY_PROFILE, {
      workplaceId,
      trigger: "Chat Header",
    });
    history.push(generatePath(SHIFT_DISCOVERY_WORKPLACE_PROFILE_FULL_PATH, { workplaceId }));
  };

  const handleGoBack = () => {
    if (isDefined(location.key)) {
      history.goBack();
    } else {
      history.push(fallbackGoBackPath);
    }
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        paddingBottom: 4,
        paddingX: 5,
        width: "100%",
      }}
    >
      <IconButton iconType="chevron-left" variant="outlined" onClick={handleGoBack} />

      <Stack
        spacing={1}
        paddingX={1}
        minWidth={0}
        alignItems="center"
        sx={{ flex: 1, justifySelf: "center" }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ maxWidth: "100%" }}
          onClick={handleViewFacilityProfile}
        >
          <Text
            variant={getTitleVariant(workplaceName)}
            sx={{
              color: (theme) => theme.palette.text.primary,
              fontWeight: 500,
              textAlign: "center",
            }}
          >
            {workplaceName}
          </Text>

          <CbhIcon type="chevron-right" size="xSmall" />
        </Stack>

        <Text
          variant="caption"
          sx={{
            color: (theme) => theme.palette.text.secondary,
            fontWeight: 500,
          }}
          onClick={handleViewFacilityProfile}
        >
          {channelShiftTime}
        </Text>
      </Stack>

      {/* Placeholder so the button and facility name can be positioned left and center
      respectively using space-between */}
      <Box sx={{ visibility: "hidden" }}>
        <IconButton iconType="chevron-left" variant="outlined" />
      </Box>
    </Stack>
  );
}
